import { graphql } from "@acto/gql";
import { TodoType } from "@acto/gql/graphql";
import { useMemo } from "react";
import { useQuery } from "urql";
import { TodoSummary } from "@acto/taskpane/core/domain/TeamEmployee";

const EmployeeTodosQuery = graphql(`
  query GetEmployeeTodos($employeeId: ID!, $todoTypes: [TodoType!]) {
    team {
      member(employeeId: $employeeId, todoTypes: $todoTypes) {
        employee {
          id
        }
        todos {
          edges {
            node {
              id
              title
              description
              dueDate
              createdAt
              client {
                __typename
                id
                name
                externalReference
              }
            }
          }
        }
      }
    }
  }
`);

interface UseEmployeeTodosOptions {
  employeeId: string | null;
  todoTypes?: TodoType[];
}

export interface UseEmployeeTodosResult {
  todos: TodoSummary[];
  isFetching: boolean;
  isPending: boolean;
}

export function useEmployeeTodos({ employeeId, todoTypes }: UseEmployeeTodosOptions): UseEmployeeTodosResult {
  const [result] = useQuery({
    query: EmployeeTodosQuery,
    variables: {
      employeeId: employeeId ?? "",
      todoTypes,
    },
    pause: !employeeId,
    requestPolicy: "cache-first",
  });

  const { data, fetching: isFetching } = result;
  const isPending = isFetching && !data;

  const todos = useMemo(() => {
    const todoEdges = data?.team?.member?.todos?.edges ?? [];
    return todoEdges.map((edge) => TodoSummary.fromFragment(edge.node));
  }, [data]);

  return {
    todos,
    isFetching,
    isPending,
  };
}
